// Timetable data hook with TanStack Query

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTimetableRecordsDirect,
  getByClassDirect,
  getByTeacherDirect,
  getBySubjectDirect,
  createTimetableRecordDirect,
  updateTimetableRecordDirect,
  deleteTimetableRecordDirect,
  bulkCreateTimetableRecordsDirect,
} from '@timetable/api'
import type {
  CreateTimetableRecordRequest,
  UpdateTimetableRecordRequest,
  BulkCreateTimetableRecordsRequest,
  TimetableFilters,
} from '@timetable/types'

// Query keys
export const timetableKeys = {
  all: ['timetable'] as const,
  lists: () => [...timetableKeys.all, 'list'] as const,
  list: (filters: TimetableFilters) => [...timetableKeys.lists(), filters] as const,
  details: () => [...timetableKeys.all, 'detail'] as const,
  detail: (id: string) => [...timetableKeys.details(), id] as const,
  byClass: (classSectionId: string) => [...timetableKeys.all, 'class', classSectionId] as const,
  byTeacher: (teacherId: string) => [...timetableKeys.all, 'teacher', teacherId] as const,
  bySubject: (subjectId: string) => [...timetableKeys.all, 'subject', subjectId] as const,
}

// Get timetable records with filters
export function useTimetableRecords(filters: TimetableFilters = {}) {
  return useQuery({
    queryKey: timetableKeys.list(filters),
    queryFn: () => getTimetableRecordsDirect(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Get timetable by class
export function useTimetableByClass(classSectionId: string) {
  return useQuery({
    queryKey: timetableKeys.byClass(classSectionId),
    queryFn: () => getByClassDirect(classSectionId),
    staleTime: 5 * 60 * 1000,
    enabled: !!classSectionId,
  })
}

// Get timetable by teacher
export function useTimetableByTeacher(teacherId: string) {
  return useQuery({
    queryKey: timetableKeys.byTeacher(teacherId),
    queryFn: () => getByTeacherDirect(teacherId),
    staleTime: 5 * 60 * 1000,
    enabled: !!teacherId,
  })
}

// Get timetable by subject
export function useTimetableBySubject(subjectId: string) {
  return useQuery({
    queryKey: timetableKeys.bySubject(subjectId),
    queryFn: () => getBySubjectDirect(subjectId),
    staleTime: 5 * 60 * 1000,
    enabled: !!subjectId,
  })
}

// Create timetable record mutation
export function useCreateTimetableRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (record: CreateTimetableRecordRequest) => createTimetableRecordDirect(record),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timetableKeys.all })
    },
  })
}

// Update timetable record mutation
export function useUpdateTimetableRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateTimetableRecordRequest }) =>
      updateTimetableRecordDirect(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timetableKeys.all })
    },
  })
}

// Delete timetable record mutation
export function useDeleteTimetableRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteTimetableRecordDirect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timetableKeys.all })
    },
  })
}

// Bulk create timetable records mutation
export function useBulkCreateTimetableRecords() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: BulkCreateTimetableRecordsRequest) =>
      bulkCreateTimetableRecordsDirect(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timetableKeys.all })
    },
  })
}
